import { useEffect, useState } from 'react';

import {
    type AviationDocument,
    isAirworthinessDirective,
    isEngineeringOrder,
    isServiceBulletin,
    isTaskCard,
    isWorkOrder,
} from '@/features/end-of-lease/types';
import { LeasingDocs } from '@/models/docs';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '@/lib/utils';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { reingestDocument, updateDocumentType, usePreview } from './api';
import { LeasingDocTypeComboBox } from './leasing-doc-type-combobox';
import { DocType } from '@/models/docs';

interface DocumentCardProps {
    leasing_document: LeasingDocs;
    id: number;
    onClose: () => void;
    refetch?: () => void;
}

export function DocumentCard({
    leasing_document,
    id,
    onClose,
    refetch,
}: DocumentCardProps) {
    const { data: preview } = usePreview(id);
    const { client, enabled } = useAuthAxios();
    const [reingestLoading, setReingestLoading] = useState(false);
    const [typeLoading, setTypeLoading] = useState(false);
    const [selectedType, setSelectedType] = useState<string>(
        leasing_document.type,
    );

    useEffect(() => {
        setSelectedType(leasing_document.type);
    }, [leasing_document.type]);

    const document: AviationDocument | null = leasing_document?.meta?.raw;
    if (document === null) {
        throw new Error('Document not found');
    }

    const handleReingest = async () => {
        setReingestLoading(true);
        try {
            await reingestDocument({ client: client, docId: id });
            refetch?.();
            toast.success('Document queued to reingest!');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to reingest document');
        } finally {
            setReingestLoading(false);
        }
    };

    const handleTypeChange = async (newType: DocType) => {
        setSelectedType(newType);
        setTypeLoading(true);
        try {
            await updateDocumentType({ client, docId: id, newType });
            refetch?.();
            toast.success('Document type updated!');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update document type');
        } finally {
            setTypeLoading(false);
        }
    };

    return (
        <div className="h-full flex flex-col shadow-none">
            <div className="flex items-center justify-between p-4 border-b">
                <div className="flex flex-col items-center space-x-2 space-y-2 w-full">
                    <div className="flex items-center justify-between w-full">
                        <h2 className="text-lg font-semibold">{document.id}</h2>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
                <Tabs defaultValue="details">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="details">Details</TabsTrigger>
                        <TabsTrigger value="references">References</TabsTrigger>
                        <TabsTrigger value="approvals">Approvals</TabsTrigger>
                        <TabsTrigger value="actions">Actions</TabsTrigger>
                    </TabsList>
                    <TabsContent value="details" className="space-y-4 pt-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    <div className="flex flex-row align-center justify-between">
                                        Basic Information
                                    </div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Title
                                        </p>
                                        <p className="text-sm">
                                            {document.title || 'Untitled'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Issue Date
                                        </p>
                                        <p className="text-sm">
                                            {formatDate(document.issueDate)}
                                        </p>
                                    </div>
                                    {document.effectiveDate && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Effective Date
                                            </p>
                                            <p className="text-sm">
                                                {formatDate(
                                                    document.effectiveDate,
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {document.expiryDate && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Expiry Date
                                            </p>
                                            <p className="text-sm">
                                                {formatDate(
                                                    document.expiryDate,
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {document.issuingAuthority && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Issuing Authority
                                            </p>
                                            <p className="text-sm">
                                                {document.issuingAuthority}
                                            </p>
                                        </div>
                                    )}
                                    {document.priority && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Priority
                                            </p>
                                            <p className="text-sm capitalize">
                                                {document.priority}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {document.aircraft && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">
                                        Aircraft Information
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Type
                                            </p>
                                            <p className="text-sm">
                                                {document.aircraft.type}
                                            </p>
                                        </div>
                                        {document.aircraft.registration && (
                                            <div>
                                                <p className="text-sm font-medium text-gray-500">
                                                    Registration
                                                </p>
                                                <p className="text-sm">
                                                    {
                                                        document.aircraft
                                                            .registration
                                                    }
                                                </p>
                                            </div>
                                        )}
                                        {document.aircraft.serialNumber && (
                                            <div>
                                                <p className="text-sm font-medium text-gray-500">
                                                    Serial Number
                                                </p>
                                                <p className="text-sm">
                                                    {
                                                        document.aircraft
                                                            .serialNumber
                                                    }
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {renderTypeSpecificContent(document)}
                    </TabsContent>

                    <TabsContent value="references" className="pt-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    References
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {document.references &&
                                document.references.length > 0 ? (
                                    <ul className="space-y-2">
                                        {document.references.map(
                                            (ref, index) => (
                                                <li
                                                    key={index}
                                                    className="flex items-start space-x-2"
                                                >
                                                    <Badge className="mt-0.5">
                                                        {ref.referenceType}
                                                    </Badge>
                                                    <div>
                                                        <p className="text-sm font-medium">
                                                            {ref.referenceId}
                                                        </p>
                                                        {ref.description && (
                                                            <p className="text-sm text-gray-500">
                                                                {
                                                                    ref.description
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        No references available
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="approvals" className="pt-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    Approvals
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {document.approvals &&
                                document.approvals.length > 0 ? (
                                    <ul className="space-y-4">
                                        {document.approvals.map(
                                            (approval, index) => (
                                                <li
                                                    key={index}
                                                    className="border-b pb-3 last:border-0 last:pb-0"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="text-sm font-medium">
                                                                {approval.role}
                                                            </p>
                                                            {approval.name && (
                                                                <p className="text-sm">
                                                                    {
                                                                        approval.name
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            {approval.date && (
                                                                <p className="text-sm text-gray-500">
                                                                    {formatDate(
                                                                        approval.date,
                                                                    )}
                                                                </p>
                                                            )}
                                                            {approval.signed !==
                                                                undefined &&
                                                                approval.signed !==
                                                                    null && (
                                                                    <Badge>
                                                                        {approval.signed
                                                                            ? 'Signed'
                                                                            : 'Unsigned'}
                                                                    </Badge>
                                                                )}
                                                        </div>
                                                    </div>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        No approvals available
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="actions" className="pt-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    Document Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex flex-col space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="text-right">
                                            Ingestion Status :
                                        </div>
                                        <div>
                                            {leasing_document.meta.eol
                                                ?.ingestionStatus ? (
                                                <Badge>
                                                    {leasing_document.meta.eol.ingestionStatus.toUpperCase()}
                                                </Badge>
                                            ) : (
                                                <Badge>Unknown</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="items-center grid grid-cols-2 gap-2">
                                        <label className="text-right block font-medium">
                                            Document Type:
                                        </label>
                                        <LeasingDocTypeComboBox
                                            value={selectedType}
                                            onChange={handleTypeChange}
                                            disabled={typeLoading}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    Document Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex flex-col space-y-2">
                                    {preview && (
                                        <Button variant="outline">
                                            <a
                                                href={preview}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open PDF
                                            </a>
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        onClick={handleReingest}
                                        disabled={reingestLoading}
                                    >
                                        {reingestLoading
                                            ? 'Reingesting...'
                                            : 'Reingest Document'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function renderTypeSpecificContent(document: AviationDocument) {
    if (isWorkOrder(document) || isEngineeringOrder(document)) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                        Work Information
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        {document.estimatedManHours && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">
                                    Estimated Man Hours
                                </p>
                                <p className="text-sm">
                                    {document.estimatedManHours}
                                </p>
                            </div>
                        )}
                        {document.actualManHours && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">
                                    Actual Man Hours
                                </p>
                                <p className="text-sm">
                                    {document.actualManHours}
                                </p>
                            </div>
                        )}
                    </div>
                    {document.tasks && document.tasks.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-medium text-gray-500 mb-2">
                                Tasks
                            </p>
                            <ul className="space-y-2">
                                {document.tasks.map((task, index) => (
                                    <li
                                        key={index}
                                        className="border rounded-md p-3"
                                    >
                                        <div className="flex justify-between">
                                            <p className="text-sm font-medium">
                                                {task.taskId}
                                            </p>
                                            {task.signedOff && (
                                                <Badge>
                                                    {task.signedOff
                                                        ? 'Signed Off'
                                                        : 'Not Signed Off'}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm mt-1">
                                            {task.description}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    if (isTaskCard(document)) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                        Task Information
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {document.estimatedManHours && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">
                                    Estimated Man Hours
                                </p>
                                <p className="text-sm">
                                    {document.estimatedManHours}
                                </p>
                            </div>
                        )}
                        {document.actualManHours && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">
                                    Actual Man Hours
                                </p>
                                <p className="text-sm">
                                    {document.actualManHours}
                                </p>
                            </div>
                        )}
                    </div>
                    <p className="text-sm font-medium text-gray-500 mb-2">
                        Tasks
                    </p>
                    <ul className="space-y-3">
                        {document.tasks.map((task, index) => (
                            <li key={index} className="border rounded-md p-3">
                                <div className="flex justify-between">
                                    <p className="text-sm font-medium">
                                        {task.taskId}
                                    </p>
                                    {task.signedOff && (
                                        <Badge>
                                            {task.signedOff
                                                ? 'Signed Off'
                                                : 'Not Signed Off'}
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-sm mt-1">
                                    {task.description}
                                </p>
                                {task.steps && task.steps.length > 0 && (
                                    <div className="mt-3 pl-3 border-l-2 border-gray-200">
                                        <p className="text-xs font-medium text-gray-500 mb-1">
                                            Steps
                                        </p>
                                        <ul className="space-y-1">
                                            {task.steps.map(
                                                (step, stepIndex) => (
                                                    <li
                                                        key={stepIndex}
                                                        className="text-xs flex items-start"
                                                    >
                                                        <span className="font-medium mr-1">
                                                            {step.stepNumber}.
                                                        </span>
                                                        <span>
                                                            {step.instruction}
                                                        </span>
                                                        {step.completed && (
                                                            <Badge className="ml-2 text-xs">
                                                                {step.completed
                                                                    ? 'Completed'
                                                                    : 'Incomplete'}
                                                            </Badge>
                                                        )}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>
        );
    }

    if (isServiceBulletin(document) || isAirworthinessDirective(document)) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                        Compliance Information
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {document.compliance && (
                        <div className="grid grid-cols-2 gap-2">
                            {document.compliance.category && (
                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Category
                                    </p>
                                    <p className="text-sm capitalize">
                                        {document.compliance.category}
                                    </p>
                                </div>
                            )}
                            {document.compliance.timeframe && (
                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Timeframe
                                    </p>
                                    <p className="text-sm">
                                        {document.compliance.timeframe}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {isServiceBulletin(document) &&
                        document.estimatedManHours && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-500">
                                    Estimated Man Hours
                                </p>
                                <p className="text-sm">
                                    {document.estimatedManHours}
                                </p>
                            </div>
                        )}
                </CardContent>
            </Card>
        );
    }

    return null;
}
